import { useState, useMemo, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Box, keyframes } from "@mui/material";
import FollowupHeader from "./FollowupHeader";
import FollowupTable from "./FollowupTable";
import CreateTaskModal from "./CreateTaskModal";
import RescheduleModal from "./RescheduleModal";
import { getUsersByCapabilitiesApi } from "../../../api/users.api";
import { rescheduleLeadFollowupApi, markFollowupCompletedApi } from "../../../api/leads.api";

// Page fade-in animation
const pageAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Helper to format time from date (using UTC for consistency)
const formatTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const hours = d.getUTCHours();
  const minutes = d.getUTCMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

// Helper to get time ago label
const getTimeLabel = (dueDate) => {
  if (!dueDate) return "";
  const now = new Date();
  const due = new Date(dueDate);
  const diffMs = due - now;
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);

  if (diffMs < 0) return "Overdue";
  if (diffMins < 60) return `in ${diffMins} mins`;
  if (diffHours < 24) return `in ${diffHours} hours`;
  return `in ${Math.round(diffHours / 24)} days`;
};

// Helper to get last interaction text from call logs
const getLastInteraction = (callLogs) => {
  if (!callLogs || callLogs.length === 0) return "No interactions yet";
  const lastLog = callLogs[callLogs.length - 1];
  const date = new Date(lastLog.createdAt || lastLog.date);
  const now = new Date();
  const diffDays = Math.round((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Contacted today";
  if (diffDays === 1) return "Contacted yesterday";
  return `Contacted ${diffDays} days ago`;
};

const DailyFollowups = () => {
  const navigate = useNavigate();
  const { allLeads = [], leads = [], updateTask, deleteTask, updateLeadField, setLeads } = useOutletContext() || {};

  // Use allLeads for followups to include all leads regardless of type
  const followupLeads = allLeads.length > 0 ? allLeads : leads;

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return { start: new Date(d), end: new Date(d) };
  });
  const [page, setPage] = useState(1);
  const [telecallersList, setTelecallersList] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [nameToIdMap, setNameToIdMap] = useState({});

  // Fetch telecallers list
  useEffect(() => {
    const fetchTelecallers = async () => {
      try {
        const res = await getUsersByCapabilitiesApi(["lead_management"]);
        const users = res?.data?.data || [];
        // Store names for dropdown
        setTelecallersList(users.map((u) => u.name).filter(Boolean));
        // Create maps for lookups
        const idToName = {};
        const nameToId = {};
        users.forEach((u) => {
          if (u._id && u.name) {
            idToName[u._id] = u.name;
            nameToId[u.name] = u._id;
          }
        });
        setUsersMap(idToName);
        setNameToIdMap(nameToId);
      } catch (err) {
        console.error("Failed to fetch telecallers:", err);
      }
    };
    fetchTelecallers();
  }, []);

  // Socket listeners for followup events are now centralized in LeadsLayout.jsx
  // which updates the shared leads state automatically

  // Extract followups from leads using next_followup field
  const tasks = useMemo(() => {
    const allFollowups = [];

    // Filter leads that have next_followup set (including completed ones)
    followupLeads.forEach((lead) => {
      if (!lead.next_followup) return;

      const dueDate = new Date(lead.next_followup);

      // Filter by date range
      if (dateRange) {
        const start = new Date(dateRange.start);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.end);
        end.setHours(23, 59, 59, 999);
        if (dueDate < start || dueDate > end) return;
      }

      const now = new Date();
      const isOverdue = dueDate < now;

      // Get telecaller name - handle both populated object and ID string
      const ae = lead.assign_employee;
      let telecallerName = null;
      let telecallerId = null;

      if (ae && typeof ae === "object") {
        // Populated object
        telecallerName = ae.name || ae.label || ae.full_name;
        telecallerId = ae._id || ae.id;
      } else if (typeof ae === "string" && ae) {
        // Just an ID - look up in usersMap
        telecallerName = usersMap[ae] || null;
        telecallerId = ae;
      }

      allFollowups.push({
        id: lead._id,
        leadId: lead._id,
        dueTime: formatTime(dueDate),
        dueDate: dueDate,
        isOverdue,
        timeAgo: getTimeLabel(dueDate),
        priority: lead.priority || "Medium",
        clientName: lead.custom_fields?.full_name || (() => {
          const raw = lead.custom_fields?.meta_raw_field_data;
          if (Array.isArray(raw)) {
            const f = raw.find((item) => item?.name === "full_name" || item?.name === "name");
            if (f?.values?.[0]) return String(f.values[0]).trim();
          }
          return null;
        })() || (lead.client_name !== "Meta Lead" ? lead.client_name : null) || lead.name || "Unknown",
        contact: lead.contact || lead.phone,
        email: lead.email,
        address: lead.address || lead.location || "",
        lastInteraction: getLastInteraction(lead.call_logs),
        telecaller: telecallerName,
        telecallerId: telecallerId,
        followup_status: lead.followup_status || "pending",
        status: lead.followup_status === "completed" ? "Completed" :
                 lead.followup_status === "rescheduled" ? "Rescheduled" :
                 isOverdue ? "Overdue" : "Pending",
        statusColor: lead.followup_status === "completed" ? "#10B981" :
                     lead.followup_status === "rescheduled" ? "#8B5CF6" :
                     isOverdue ? "#EF4444" : "#F59E0B",
        timeLabel: lead.followup_status === "completed" ? "Completed" :
                   lead.followup_status === "rescheduled" ? "Rescheduled" :
                   getTimeLabel(dueDate),
        timeLabelColor: lead.followup_status === "completed" ? "#10B981" :
                         lead.followup_status === "rescheduled" ? "#8B5CF6" :
                         isOverdue ? "#EF4444" : "#F59E0B",
        notes: lead.notes || lead.remarks,
        leadStatus: lead.status,
      });
    });

    // Sort by due date (overdue first, then upcoming)
    allFollowups.sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      if (a.dueDate && b.dueDate) return a.dueDate - b.dueDate;
      return 0;
    });

    return allFollowups;
  }, [followupLeads, dateRange, usersMap]);

  // Modal states
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedTaskForReschedule, setSelectedTaskForReschedule] = useState(null);

  const rowsPerPage = 10;

  // Calculate stats across all followups (not just selected date)
  const stats = useMemo(() => {
    const now = new Date();
    const todayStr = now.toLocaleDateString("en-CA");

    let overdue = 0;
    let dueToday = 0;
    let upcoming = 0;

    followupLeads.forEach((lead) => {
      if (!lead.next_followup) return;
      // Skip completed followups from stats
      if (lead.followup_status === "completed") return;

      const dueDate = new Date(lead.next_followup);
      const dueDateStr = dueDate.toLocaleDateString("en-CA");

      if (dueDate < now && dueDateStr !== todayStr) {
        overdue++;
      } else if (dueDateStr === todayStr) {
        dueToday++;
      } else if (dueDate > now) {
        upcoming++;
      }
    });

    return { overdue, dueToday, upcoming };
  }, [followupLeads]);

  // Filter followups based on search and priority
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (task) =>
          task.clientName?.toLowerCase().includes(query) ||
          task.telecaller?.toLowerCase().includes(query) ||
          task.contact?.toLowerCase().includes(query) ||
          task.email?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [tasks, searchQuery]);

  // Paginated tasks
  const paginatedTasks = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredTasks.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredTasks, page, rowsPerPage]);

  // Handlers
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleDateChange = (date, quickOption, range) => {
    if (range) {
      setDateRange({ start: range.start, end: range.end });
    }
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleNewTask = () => {
    setCreateTaskModalOpen(true);
  };

  const handleCreateTaskClose = () => {
    setCreateTaskModalOpen(false);
  };

  const handleCreateTaskSubmit = (taskData) => {
    // Update local leads state so the new task appears in the table immediately
    if (setLeads && taskData.leadId) {
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead._id === taskData.leadId
            ? {
                ...lead,
                next_followup: taskData.scheduledDate,
                followup_status: "pending",
                priority: taskData.priority || lead.priority,
              }
            : lead
        )
      );
    }
    setCreateTaskModalOpen(false);
  };

  const handleReschedule = (task) => {
    setSelectedTaskForReschedule(task);
    setRescheduleModalOpen(true);
  };

  const handleRescheduleClose = () => {
    setRescheduleModalOpen(false);
    setSelectedTaskForReschedule(null);
  };

  const handleRescheduleSubmit = async (rescheduleData) => {
    try {
      const task = selectedTaskForReschedule;
      if (!task || !task.leadId) return;

      // Call the new reschedule lead followup API
      const res = await rescheduleLeadFollowupApi(task.leadId, {
        newDate: rescheduleData.newDate,
        reason: rescheduleData.rescheduleReason,
        priority: rescheduleData.priority,
        notes: rescheduleData.notes,
        preferredTime: rescheduleData.preferredTime,
        duration: rescheduleData.duration,
      });

      // Update the local state with the new data from the response
      if (setLeads && res?.data?.data) {
        setLeads((prevLeads) =>
          prevLeads.map((lead) =>
            lead._id === task.leadId
              ? {
                  ...lead,
                  next_followup: res.data.data.next_followup,
                  followup_status: res.data.data.followup_status || "rescheduled",
                }
              : lead
          )
        );
      }
    } catch (err) {
      console.error("Failed to reschedule follow-up:", err);
    }
    setRescheduleModalOpen(false);
    setSelectedTaskForReschedule(null);
  };

  const handleMarkDone = async (task) => {
    try {
      if (!task || !task.leadId) return;

      // Call the new mark follow-up completed API
      await markFollowupCompletedApi(task.leadId, {
        notes: task.notes || "",
      });

      // Update the local state to mark as completed
      if (setLeads) {
        setLeads((prevLeads) =>
          prevLeads.map((lead) =>
            lead._id === task.leadId
              ? { ...lead, followup_status: "completed" }
              : lead
          )
        );
      }
    } catch (err) {
      console.error("Failed to mark follow-up as done:", err);
    }
  };

  const handleViewDetails = (task) => {
    // Navigate to lead details page
    const leadId = task.leadId || task._id || task.id;
    if (leadId) {
      navigate(`/leads/${leadId}`);
    }
  };

  const handleTelecallerChange = async (task, telecallerName) => {
    try {
      if (!task || !task.leadId) return;

      // Look up user ID from name
      const userId = nameToIdMap[telecallerName];
      if (!userId && telecallerName !== "Unassigned") {
        console.error("Could not find user ID for:", telecallerName);
        return;
      }

      // Use updateLeadField to update the assigned employee
      if (updateLeadField) {
        await updateLeadField(task.leadId, "assign_employee", userId || null);
      }
    } catch (err) {
      console.error("Failed to update telecaller:", err);
    }
  };

  const handleLeadPriorityChange = async (task, priority) => {
    try {
      if (!task || !task.leadId) return;

      // Use updateLeadField from context to update the priority
      if (updateLeadField) {
        await updateLeadField(task.leadId, "priority", priority);
      }
    } catch (err) {
      console.error("Failed to update priority:", err);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        animation: `${pageAnimation} 0.4s ease-out`,
      }}
    >
      <FollowupHeader
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onDateChange={handleDateChange}
        onNewTask={handleNewTask}
        totalPendingTasks={filteredTasks.length}
        stats={stats}
      />

      <Box sx={{ p: 2.5 }}>
        <FollowupTable
          tasks={paginatedTasks}
          page={page}
          onPageChange={handlePageChange}
          totalTasks={filteredTasks.length}
          rowsPerPage={rowsPerPage}
          onReschedule={handleReschedule}
          onMarkDone={handleMarkDone}
          onViewDetails={handleViewDetails}
          onTelecallerChange={handleTelecallerChange}
          onPriorityChange={handleLeadPriorityChange}
          telecallers={telecallersList}
          searchQuery={searchQuery}
        />
      </Box>

      {/* Create Task Modal */}
      <CreateTaskModal
        open={createTaskModalOpen}
        onClose={handleCreateTaskClose}
        onSubmit={handleCreateTaskSubmit}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        open={rescheduleModalOpen}
        onClose={handleRescheduleClose}
        task={selectedTaskForReschedule}
        onSubmit={handleRescheduleSubmit}
      />
    </Box>
  );
};

export default DailyFollowups;
